import { LoadingButton } from '@mui/lab';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Stack,
    Switch,
    Typography,
    useTheme,
} from '@mui/material';
import {
    DataGrid,
    GridColDef,
    GridColumnHeaderParams,
    GridRenderCellParams,
    GridRowsProp,
} from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../api/Api';
import { RequestSnackbar, useRequest } from '../api/Request';
import { AuthStatus, useAuth, useRequiredAuth } from '../auth/Auth';
import LoadingPage from '../loading/LoadingPage';
import Avatar from '../profile/info/Avatar';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SlideUpTransition from '../components/SlideUpTransition';
import ConnectStripeAccount from './payment/ConnectStripeAccount';
import StripeInfo from './payment/StripeInfo';
import { ListUsersResponse, SubscriptionStatus } from '../database/user';
import { useCache } from '../api/cache/Cache';

const HomePage = () => {
    const auth = useAuth();
    const { user } = useRequiredAuth();
    const theme = useTheme();
    const api = useApi();
    const request = useRequest<ListUsersResponse>();
    const [rows, setRows] = useState<GridRowsProp>([]);
    // const [lastStartKey, setLastStartKey] = useState('');
    const [data, setData] = useState<ListUsersResponse>();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [userToDelete, setUserToDelete] = useState<string>('');
    const [idToDelete, setIdToDelete] = useState<number>(0);
    const cache = useCache();

    const deleteUser = (username: string, index: number) => {
        api.deleteUserPublic(username);
        const newData = data?.users || [];
        setData({
            users: [...newData.slice(0, index), ...newData.slice(index + 1)],
            lastEvaluatedKey: data?.lastEvaluatedKey || '',
        });
        if (cache.userSummaries.get(username)) {
            cache.userSummaries.remove(username);
        }
        setShowDeleteDialog(false);
        setRows((prevRows) => prevRows.filter((row) => row.id !== index + 1));
    };

    const handleSubscriptionOverrideToggle = (
        username: string,
        currentValue: boolean,
    ) => {
        const newValue = !currentValue;
        api.updateUserSubscriptionOverride(username, newValue)
            .then(() => {
                // Update the local state
                const updatedUsers = data?.users.map((u) =>
                    u.username === username
                        ? { ...u, subscriptionOverride: newValue }
                        : u,
                );
                setData({
                    users: updatedUsers || [],
                    lastEvaluatedKey: data?.lastEvaluatedKey,
                });
                // Update the rows
                setRows((prevRows) =>
                    prevRows.map((row) =>
                        row.subscriptionOverride?.[0] === username
                            ? { ...row, subscriptionOverride: [username, newValue] }
                            : row,
                    ),
                );
            })
            .catch((err) => {
                console.error('Failed to update subscription override:', err);
            });
    };

    const handleResponse = useCallback(
        (resp: ListUsersResponse) => {
            // setLastStartKey(data?.lastEvaluatedKey || '');

            // const seen: Record<string, boolean> = {};
            const newEntries = (data?.users || []).concat(
                resp.users.sort((lhs, rhs) => rhs.createdAt.localeCompare(lhs.createdAt)),
            );
            // .filter((e) => {
            //     return seen.hasOwnProperty(e.id) ? false : (seen[e.id] = true);
            // });

            setData({
                users: newEntries,
            });
        },
        [data],
    );

    const columns: GridColDef[] = [
        {
            field: 'id',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Typography variant='body2' sx={{ fontFamily: 'LemonMilk' }}>
                    {'#'}
                </Typography>
            ),
            maxWidth: 100,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'avatar',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Typography variant='body2' sx={{ fontFamily: 'LemonMilk' }}>
                    {'Avatar'}
                </Typography>
            ),
            minWidth: 125,
            renderCell: (params: GridRenderCellParams<any, Array<string>>) => {
                if (!params.value) {
                    return;
                }
                return (
                    <Avatar
                        username={params.value[0]}
                        displayName={params.value[1]}
                        size={50}
                    />
                );
            },
            headerAlign: 'center',
            align: 'center',
        },
        {
            field: 'displayName',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Typography variant='body2' sx={{ fontFamily: 'LemonMilk' }}>
                    {'Display Name'}
                </Typography>
            ),
            minWidth: 225,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'bio',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Typography variant='body2' sx={{ fontFamily: 'LemonMilk' }}>
                    {'Bio'}
                </Typography>
            ),
            minWidth: 270,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'createdAt',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Typography variant='body2' sx={{ fontFamily: 'LemonMilk' }}>
                    {'Created At'}
                </Typography>
            ),
            minWidth: 225,
            valueFormatter: (value: string) => {
                const formattedDate = new Date(value).toLocaleDateString();
                const formattedTime = new Date(value).toLocaleTimeString([], {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                });
                const formattedDateTime = `${formattedDate} @ ${formattedTime}`;
                return formattedDateTime;
            },
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'subscriptionStatus',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Typography variant='body2' sx={{ fontFamily: 'LemonMilk' }}>
                    {'Subscription Status'}
                </Typography>
            ),
            minWidth: 180,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: (value: SubscriptionStatus) => {
                switch (value) {
                    case SubscriptionStatus.FREE_TIER:
                        return 'Free Tier';
                    case SubscriptionStatus.SUBSCRIBED:
                        return 'Subscribed';
                    case SubscriptionStatus.CANCELED:
                        return 'Canceled';
                }
            },
        },
        {
            field: 'subscriptionOverride',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Typography variant='body2' sx={{ fontFamily: 'LemonMilk' }}>
                    {'Free Subscription'}
                </Typography>
            ),
            minWidth: 180,
            renderCell: (params: GridRenderCellParams<any, Array<any>>) => {
                if (!params.value) {
                    return null;
                }
                const username = params.value[0];
                const isOverridden = params.value[1] || false;
                return (
                    <Switch
                        checked={isOverridden}
                        onChange={() =>
                            handleSubscriptionOverrideToggle(username, isOverridden)
                        }
                        color='primary'
                    />
                );
            },
            headerAlign: 'center',
            align: 'center',
        },
        {
            field: 'deleteUser',
            renderHeader: (params: GridColumnHeaderParams) => (
                <Typography variant='body2' sx={{ fontFamily: 'LemonMilk' }}>
                    {'Delete User'}
                </Typography>
            ),
            minWidth: 150,
            renderCell: (params: GridRenderCellParams<any, Array<string>>) => {
                if (!params.value) {
                    return;
                }
                const username = params.value[0];
                const index = params.value[1];
                return (
                    <Button
                        variant='contained'
                        onClick={() => {
                            setUserToDelete(username);
                            setIdToDelete(Number(index));
                            setShowDeleteDialog(true);
                        }}
                    >
                        Delete User
                    </Button>
                );
            },
            headerAlign: 'center',
            align: 'center',
        },
    ];

    useEffect(() => {
        if (!request.isSent()) {
            request.onStart();
            const fetchAllUsers = async () => {
                const allUsers: any[] = [];
                let startKey: string | undefined;

                do {
                    const response = await api.listUsersPublic(startKey);
                    allUsers.push(...response.data.users);
                    startKey = response.data.lastEvaluatedKey;
                } while (startKey);

                return allUsers;
            };

            fetchAllUsers()
                .then((users) => {
                    handleResponse({ users, lastEvaluatedKey: undefined });
                    const newRows = users.map((user, i) => ({
                        id: i + 1,
                        avatar: [user.username, user.displayName],
                        displayName: user.displayName,
                        bio: user.bio,
                        createdAt: user.createdAt,
                        subscriptionStatus: user.subscriptionStatus,
                        subscriptionOverride: [user.username, user.subscriptionOverride],
                        deleteUser: [user.username, i],
                    }));
                    setRows(newRows);
                    request.onSuccess({ users, lastEvaluatedKey: undefined });
                })
                .catch((err) => {
                    console.error(err);
                    request.onFailure(err);
                });
        }
    }, [request, api, handleResponse]);

    if (auth.status === AuthStatus.Loading) {
        return <LoadingPage />;
    }

    return (
        <Container sx={{ pt: 6, mb: '100px' }}>
            {user.isArtist &&
                (user.artistInfo?.stripeAccountId ? (
                    <>
                        <StripeInfo />
                    </>
                ) : (
                    <ConnectStripeAccount />
                ))}

            <Dialog
                open={showDeleteDialog}
                onClose={
                    request.isLoading() ? undefined : () => setShowDeleteDialog(false)
                }
                TransitionComponent={SlideUpTransition}
                PaperProps={{
                    sx: {
                        borderColor: 'rgba(211, 47, 47, 0.4)',
                        boxShadow: '0 24px 64px rgba(0,0,0,0.85), 0 0 0 1px rgba(211,47,47,0.2)',
                    },
                }}
            >
                <RequestSnackbar request={request} />
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningAmberIcon sx={{ color: 'error.main', fontSize: '1.3rem' }} />
                    Delete this user?
                    <IconButton
                        aria-label='close'
                        onClick={() => setShowDeleteDialog(false)}
                        sx={{ position: 'absolute', right: 10, top: 8 }}
                        disabled={request.isLoading()}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        sx={{
                            color: `${theme.palette.secondary.main}`,
                        }}
                    >
                        Are you sure you want to delete this user? You can't undo this
                        action.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        color='secondary'
                        onClick={() => setShowDeleteDialog(false)}
                        disabled={request.isLoading()}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        color='error'
                        variant='contained'
                        onClick={() => deleteUser(userToDelete, idToDelete)}
                        loading={request.isLoading()}
                    >
                        Delete User
                    </LoadingButton>
                </DialogActions>
            </Dialog>
            <Card sx={{ pt: 3 }}>
                <RequestSnackbar request={request} />
                <CardHeader title='User Management' />
                <CardContent>
                    {request.isLoading() ? (
                        <Stack justifyContent='center' alignItems='center' padding={20}>
                            <CircularProgress />
                        </Stack>
                    ) : (
                        <DataGrid
                            rows={rows}
                            getRowHeight={() => 'auto'}
                            columns={columns}
                            sx={{
                                '&.MuiDataGrid-root': {
                                    bgcolor: `${theme.palette.secondary.light}`,
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    backgroundColor: `${theme.palette.primary.main}`,
                                },
                                '& .MuiDataGrid-columnHeaderTitle': {
                                    color: 'inherit',
                                },
                                '& .MuiDataGrid-iconButtonContainer': {
                                    color: 'inherit',
                                },
                                '& .MuiDataGrid-menuIcon': {
                                    color: 'inherit',
                                },
                                '& .MuiDataGrid-sortIcon': {
                                    color: 'inherit',
                                },
                                '& .MuiIconButton-root': {
                                    color: 'inherit',
                                },
                                '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus':
                                    {
                                        outline: 'none',
                                    },
                                '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
                                    py: '8px',
                                },
                                '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
                                    py: '15px',
                                },
                                '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell':
                                    {
                                        py: '22px',
                                    },
                            }}
                        />
                    )}
                </CardContent>
            </Card>
        </Container>
    );
};

export default HomePage;
